<script lang="ts">
  /* Mandatory code wall shown straight after CREATE ACCOUNT.

     Signup does not open a session — the backend only does that once this
     screen posts the right code — so there is nothing to skip past here. The
     address is read from sessionStorage (see $lib/verifyEmail) rather than the
     URL. Landing here without one (bookmark, reopened tab) falls back to
     asking for it, which also covers "I registered in another browser": typing
     the address and hitting resend re-arms the same wall. */
  import { onMount, tick } from "svelte";
  import { _ } from "svelte-i18n";
  import { push } from "svelte-spa-router";
  import LanguageSwitcher from "$components/LanguageSwitcher.svelte";
  import { get } from "svelte/store";
  import { ApiError } from "$lib/api";
  import { auth, loadSession } from "$lib/auth";
  import { homePath } from "$lib/surfaces";
  import { locale } from "$lib/i18n";
  import {
    clearPendingVerificationEmail,
    confirmVerificationCode,
    getPendingVerificationEmail,
    requestVerificationCode,
    setPendingVerificationEmail,
  } from "$lib/verifyEmail";

  const CODE_LENGTH = 4;

  let email = $state("");
  let digits = $state<string[]>(Array(CODE_LENGTH).fill(""));
  let inputs = $state<(HTMLInputElement | null)[]>(Array(CODE_LENGTH).fill(null));
  let emailInput = $state<HTMLInputElement | null>(null);
  let busy = $state(false);
  let error = $state("");
  let notice = $state("");

  const code = $derived(digits.join(""));
  const complete = $derived(code.length === CODE_LENGTH && !digits.some((d) => d === ""));

  onMount(async () => {
    email = getPendingVerificationEmail();
    await tick();
    // Auto-focus the first box, per spec. Without a known address the email
    // field comes first, so focus that instead.
    (email ? inputs[0] : emailInput)?.focus();
  });

  function setDigit(index: number, value: string) {
    const digit = value.replace(/\D/g, "").slice(-1);
    digits[index] = digit;
    error = "";
    if (digit && index < CODE_LENGTH - 1) inputs[index + 1]?.focus();
  }

  function onKeydown(e: KeyboardEvent, index: number) {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      // Backspace on an empty box steps back and clears the previous one, so
      // holding it wipes the whole code the way people expect.
      e.preventDefault();
      digits[index - 1] = "";
      inputs[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputs[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputs[index + 1]?.focus();
    } else if (e.key === "Enter" && complete) {
      submit();
    }
  }

  /** Pasting the whole code into any box fills the row. */
  function onPaste(e: ClipboardEvent, index: number) {
    const pasted = (e.clipboardData?.getData("text") ?? "").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    for (let i = 0; i < CODE_LENGTH - index; i++) {
      digits[index + i] = pasted[i] ?? digits[index + i];
    }
    inputs[Math.min(index + pasted.length, CODE_LENGTH - 1)]?.focus();
  }

  async function submit() {
    if (!complete || busy || !email.trim()) return;
    busy = true;
    error = "";
    notice = "";
    try {
      await confirmVerificationCode(email.trim(), code);
      clearPendingVerificationEmail();
      // The confirm call established the session, so read it back and route
      // into the wizard exactly like a fresh signup used to.
      await loadSession();
      const state = get(auth);
      const slug = state.memberships[0]?.tenant.slug;
      // The short workspace skips the wizard: its signup marked it done.
      if (slug && state.user?.onboarding_completed_at) push(homePath(state, slug));
      else push(slug ? `/t/${slug}/onboarding` : "/");
    } catch (err) {
      error = err instanceof ApiError ? err.message : $_("verify.error.generic");
      digits = Array(CODE_LENGTH).fill("");
      await tick();
      inputs[0]?.focus();
    } finally {
      busy = false;
    }
  }

  async function resend() {
    if (busy || !email.trim()) return;
    busy = true;
    error = "";
    try {
      await requestVerificationCode(email.trim(), $locale ?? "en");
      setPendingVerificationEmail(email.trim());
      notice = $_("verify.resent");
    } catch {
      error = $_("verify.error.generic");
    } finally {
      busy = false;
    }
  }
</script>

<div class="auth-page">
  <div class="auth-card">
    <div class="lang-row"><LanguageSwitcher /></div>

    <div class="brand">
      <span class="material-icons-outlined brand-icon">mark_email_unread</span>
      <h2>{$_("verify.title")}</h2>
      <p class="subtitle">
        {#if email}
          {$_("verify.lead", { values: { email } })}
        {:else}
          {$_("verify.leadNoEmail")}
        {/if}
      </p>
    </div>

    {#if !email}
      <div class="field">
        <label for="ve-email">{$_("auth.email")}</label>
        <input
          id="ve-email"
          type="email"
          bind:this={emailInput}
          bind:value={email}
          autocomplete="email"
          placeholder={$_("auth.email.placeholder")}
        />
      </div>
    {/if}

    <div class="code-row" role="group" aria-label={$_("verify.title")}>
      {#each digits as digit, i}
        <input
          class="code-box"
          class:filled={!!digit}
          type="text"
          inputmode="numeric"
          autocomplete={i === 0 ? "one-time-code" : "off"}
          maxlength="1"
          aria-label={$_("verify.digitLabel", { values: { n: i + 1 } })}
          bind:this={inputs[i]}
          value={digit}
          oninput={(e) => setDigit(i, e.currentTarget.value)}
          onkeydown={(e) => onKeydown(e, i)}
          onpaste={(e) => onPaste(e, i)}
          onfocus={(e) => e.currentTarget.select()}
          disabled={busy}
        />
      {/each}
    </div>

    {#if error}<div class="error-alert">{error}</div>{/if}
    {#if notice}<div class="notice-alert">{notice}</div>{/if}

    <button
      type="button"
      class="btn btn--primary btn--lg"
      onclick={submit}
      disabled={busy || !complete || !email.trim()}
    >
      {busy ? `${$_("common.processing")}…` : $_("verify.submit")}
    </button>

    <p class="bottom">
      <button type="button" class="link-btn" onclick={resend} disabled={busy || !email.trim()}>
        {$_("verify.resend")}
      </button>
      <span class="sep">·</span>
      <a href="#/login">{$_("auth.reset.backToLogin")}</a>
    </p>
  </div>
</div>

<style>
  .auth-page {
    min-height: 100vh;
    display: flex; align-items: center; justify-content: center;
    background: var(--color-bg); padding: 20px;
  }
  .auth-card {
    width: 100%; max-width: 440px; background: #fff; padding: 32px 40px 36px;
    border-radius: 12px; box-shadow: 0 10px 25px rgba(0,0,0,0.05);
    display: flex; flex-direction: column; gap: 20px;
  }
  .lang-row { display: flex; justify-content: flex-end; }
  .brand { text-align: center; }
  .brand-icon { font-size: 44px !important; color: var(--color-primary); }
  .brand h2 { margin: 10px 0 0; font-size: 23px; color: var(--color-surface-dark); }
  .subtitle { margin: 8px 0 0; color: var(--color-text-muted); font-size: 14px; line-height: 1.5; }

  .field { display: flex; flex-direction: column; gap: 8px; }
  .field label { font-size: 13px; font-weight: 600; color: var(--color-surface-dark); }
  .field input {
    padding: 12px; border: 1px solid var(--color-divider);
    border-radius: 6px; font-size: 15px; font-family: inherit;
  }
  .field input:focus { border-color: var(--color-primary); outline: none; }

  .code-row { display: flex; justify-content: center; gap: 12px; }
  .code-box {
    width: 58px; height: 68px; text-align: center;
    font-size: 28px; font-weight: 700; font-family: inherit;
    color: var(--color-surface-darker);
    border: 1.5px solid var(--color-divider); border-radius: 10px;
    background: var(--color-bg);
  }
  .code-box:focus {
    outline: none; border-color: var(--color-primary); background: #fff;
    box-shadow: 0 0 0 3px rgba(33,133,213,0.15);
  }
  .code-box.filled { background: #fff; border-color: var(--color-primary); }

  .btn--lg { padding: 14px; font-size: 16px; font-weight: 600; border-radius: 6px; cursor: pointer; font-family: inherit; }
  .btn--primary { background: var(--color-primary); color: #fff; border: none; }
  .btn--primary:hover:not(:disabled) { opacity: 0.9; }
  .btn:disabled { opacity: 0.55; cursor: not-allowed; }

  .error-alert {
    padding: 11px; background: #fff5f5; border: 1px solid #ffc1c1;
    color: #e53935; border-radius: 6px; font-size: 14px; text-align: center;
  }
  .notice-alert {
    padding: 11px; background: #f1f8f3; border: 1px solid #bfe0c8;
    color: #2e7d32; border-radius: 6px; font-size: 14px; text-align: center;
  }

  .bottom { text-align: center; margin: 0; font-size: 13px; color: var(--color-text-muted); }
  .sep { margin: 0 8px; }
  .link-btn {
    border: none; background: none; padding: 0; cursor: pointer;
    color: var(--color-primary); font: inherit; font-size: 13px; text-decoration: underline;
  }
  .link-btn:disabled { opacity: 0.55; cursor: not-allowed; }
</style>
